import { defineStore } from 'pinia';
import { EventManagementData } from '@front-app/services/EventsApi/EventModel';
import { createGroupEvent } from '@front-app/services/GroupsApi/GroupsEventsApi';
import { getAdminEvent, updateEvent, UpsertEventRequest } from '@front-app/services/EventsApi/EventsApi';
import { UseEventStore, useEventStore } from './Event';

interface StoreState {
	events: EventManagementData[];
}

interface EventAdminStoreDependencies {
	eventStore: UseEventStore;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const createEventAdminStore = (name: string, dependencies: EventAdminStoreDependencies) => defineStore(name, {
	state: (): StoreState => ({
		events: [],
	}),
	getters: {
		getById(state) {
			return (requestedEventId: string) => state.events
				.find(({ id }) => id === requestedEventId);
		},
	},
	actions: {
		storeAdminEvents(events: EventManagementData[]) {
			this.$patch((state) => {
				const eventIds = events.map(({ id }) => id);
				const oldEvents = state.events.filter(({ id }) => !eventIds.includes(id));
				state.events = oldEvents.concat(events);
			});
		},
		// For now, only event creation is tied to the group. Once created
		// any admin of the event has the ability to administer it
		// This simplifies the front-end needing to track both group and event
		// permissions
		async createGroupEvent(groupId: string, event: UpsertEventRequest): Promise<string> {
			const result = await createGroupEvent(groupId, event);
			this.storeAdminEvents([result]);
			return result.id;
		},
		async fetch(eventId: string) {
			const result = await getAdminEvent(eventId);
			this.storeAdminEvents([result]);
		},
		async update(eventId: string, updates: Partial<UpsertEventRequest>) {
			const event = this.getById(eventId);
			if (!event) {
				// eslint-disable-next-line no-console
				console.error('Cannot update an non-existent event.');
				return Promise.reject();
			}
			// eslint-disable-next-line @typescript-eslint/no-unused-vars
			const { id, groupIds, ...baseEventData } = event;
			const result = await updateEvent(eventId, { ...baseEventData, ...updates });

			// For the event store to refresh if it has this item already
			// Maybe in future we can do a hot update, without a fetch
			if (dependencies.eventStore().getById(eventId)) {
				await dependencies.eventStore().fetch(eventId);
			}

			this.storeAdminEvents([result]);

			return Promise.resolve();
		},
	},
});

export const useEventAdminStore = createEventAdminStore('eventAdmin', {
	eventStore: useEventStore,
});
